export const emptyPermissionScope = {
  projects: { read: [], upload: [], edit: [] },
  collections: { read: [], upload: [], edit: [] }
};

export const normalizeIdList = (values) => {
  if (!Array.isArray(values)) return [];

  const numericValues = values
    .map((value) => Number(value))
    .filter((parsed) => Number.isInteger(parsed) && parsed > 0);

  return Array.from(new Set(numericValues));
};

export const normalizePermissionScope = (scope) => {
  const normalized = {
    projects: { read: [], upload: [], edit: [] },
    collections: { read: [], upload: [], edit: [] }
  };
  if (!scope || typeof scope !== 'object') return normalized;

  normalized.projects.read = normalizeIdList(scope?.projects?.read);
  normalized.projects.upload = normalizeIdList(scope?.projects?.upload);
  normalized.projects.edit = normalizeIdList(scope?.projects?.edit);
  normalized.collections.read = normalizeIdList(scope?.collections?.read);
  normalized.collections.upload = normalizeIdList(scope?.collections?.upload);
  normalized.collections.edit = normalizeIdList(scope?.collections?.edit);

  ['projects', 'collections'].forEach((resource) => {
    normalized[resource].edit.forEach((id) => {
      if (!normalized[resource].upload.includes(id)) {
        normalized[resource].upload.push(id);
      }
      if (!normalized[resource].read.includes(id)) {
        normalized[resource].read.push(id);
      }
    });
  });

  return normalized;
};

export const scopeHasAnyEntry = (scope) => {
  const normalized = normalizePermissionScope(scope);
  return (
    normalized.projects.read.length > 0 ||
    normalized.projects.upload.length > 0 ||
    normalized.projects.edit.length > 0 ||
    normalized.collections.read.length > 0 ||
    normalized.collections.upload.length > 0 ||
    normalized.collections.edit.length > 0
  );
};

export const getProjectScopedAccessLevel = (scope, projectId) => {
  const normalized = normalizePermissionScope(scope);
  const normalizedProjectId = Number(projectId);

  if (!Number.isInteger(normalizedProjectId) || normalizedProjectId <= 0) {
    return 'none';
  }
  if (normalized.projects.edit.includes(normalizedProjectId)) {
    return 'edit';
  }
  if (normalized.projects.upload.includes(normalizedProjectId)) {
    return 'upload';
  }
  if (normalized.projects.read.includes(normalizedProjectId)) {
    return 'read';
  }
  return 'none';
};

export const getProjectScopedAccessFlags = (scope, projectId) => {
  const normalized = normalizePermissionScope(scope);
  const normalizedProjectId = Number(projectId);

  if (!Number.isInteger(normalizedProjectId) || normalizedProjectId <= 0) {
    return { read: false, upload: false, edit: false };
  }

  return {
    read: normalized.projects.read.includes(normalizedProjectId),
    upload: normalized.projects.upload.includes(normalizedProjectId),
    edit: normalized.projects.edit.includes(normalizedProjectId)
  };
};

export const setProjectScopedAccessLevel = (scope, projectId, accessLevel) => {
  const normalized = normalizePermissionScope(scope);
  const normalizedProjectId = Number(projectId);

  if (!Number.isInteger(normalizedProjectId) || normalizedProjectId <= 0) {
    return normalized;
  }

  ['read', 'upload', 'edit'].forEach((mode) => {
    normalized.projects[mode] = normalized.projects[mode].filter((id) => id !== normalizedProjectId);
  });

  if (accessLevel === 'read') {
    normalized.projects.read.push(normalizedProjectId);
  } else if (accessLevel === 'upload') {
    normalized.projects.upload.push(normalizedProjectId);
  } else if (accessLevel === 'edit') {
    normalized.projects.edit.push(normalizedProjectId);
  }

  return normalizePermissionScope(normalized);
};

export const setProjectScopedAccessFlags = (scope, projectId, accessFlags = {}) => {
  const normalized = normalizePermissionScope(scope);
  const normalizedProjectId = Number(projectId);

  if (!Number.isInteger(normalizedProjectId) || normalizedProjectId <= 0) {
    return normalized;
  }

  const nextFlags = {
    read: Boolean(accessFlags.read),
    upload: Boolean(accessFlags.upload),
    edit: Boolean(accessFlags.edit)
  };

  if (nextFlags.edit) {
    nextFlags.read = true;
    nextFlags.upload = true;
  }

  if (!nextFlags.upload) {
    nextFlags.edit = false;
  }

  ['read', 'upload', 'edit'].forEach((mode) => {
    normalized.projects[mode] = normalized.projects[mode].filter((id) => id !== normalizedProjectId);
  });

  if (nextFlags.read) {
    normalized.projects.read.push(normalizedProjectId);
  }
  if (nextFlags.upload) {
    normalized.projects.upload.push(normalizedProjectId);
  }
  if (nextFlags.edit) {
    normalized.projects.edit.push(normalizedProjectId);
  }

  return normalizePermissionScope(normalized);
};

export const getEffectiveProjectAccessLevel = (userRow, projectId) => {
  const permission = String(userRow?.permission || '').trim().toLowerCase();
  if (permission === 'admin' || permission === 'editor') {
    return permission;
  }
  return getProjectScopedAccessLevel(userRow?.permission_scope, projectId);
};

export const getEffectiveProjectAccessFlags = (userRow, projectId) => {
  const permission = String(userRow?.permission || '').trim().toLowerCase();
  if (permission === 'admin' || permission === 'editor') {
    return { read: true, upload: true, edit: true };
  }
  return getProjectScopedAccessFlags(userRow?.permission_scope, projectId);
};
